import React from 'react';

import { Button, Stack } from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import InvoiceCollectModal from 'components/invoices/InvoiceCollectModal';
import InvoiceSummaryCard from 'components/invoices/InvoiceSummaryCard';
import { useAuth } from 'context/auth-context';
import useInvoice from 'hooks/invoice/useInvoice';
import useSendInvoice from 'hooks/invoice/useSendInvoice';
import { useAthleteTracking } from 'hooks/mixpanel/useAthleteTracking';
import BackHeader from 'layouts/MobileLayout/components/BackHeader';
import { useParams, useSearchParams } from 'react-router-dom';
import notificationHandler from 'utils/notifications';

const InvoiceDetail = (): JSX.Element => {
  const { session } = useAuth();
  const { invoiceId } = useParams<{ invoiceId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isError } = useInvoice(invoiceId);
  const athleteTracking = useAthleteTracking();

  const [isModalOpened, { open: openModal, close: closeModal }] = useDisclosure(false);

  const { successNotification, errorNotification } = notificationHandler();
  const sendInvoiceMutation = useSendInvoice();

  // Restore modal state from URL params
  React.useEffect(() => {
    const collectModalId = searchParams.get('collectModal');
    if (collectModalId && !isModalOpened) {
      openModal();
    }
  }, [searchParams, isModalOpened, openModal]);

  const handleCloseModal = (): void => {
    // Clear collect modal URL params
    const params = new URLSearchParams(searchParams);
    params.delete('collectModal');
    params.delete('verb');
    setSearchParams(params, { replace: true });
    closeModal();
  };

  const handleSendInvoice = (): void => {
    sendInvoiceMutation.mutate(
      { invoiceId: invoiceId || '', athleteId: session?.athlete?.athleteId || '' },
      {
        onError: error => {
          errorNotification(
            'Failed to send invoice. Please try again or contact support.',
            error.response?.data.message,
          );
        },
        onSuccess: () => {
          successNotification(
            'Invoice sent to your email.',
            `Invoice with details has been sent to your email.`,
          );
        },
      },
    );
  };
  if (isError) {
    return <>Something went wrong</>;
  }

  return (
    <>
      <BackHeader title='Invoice Details' />
      <Stack className='content'>
        <Stack align='center' spacing='md' px='lg'>
          {data?.data && <InvoiceSummaryCard invoice={data.data} />}

          {data?.data && ['PAST DUE', 'FAILED PAST DUE', 'UPCOMING'].includes(data.data.status) && (
            <Button
              color='green'
              w={180}
              size='md'
              radius='xl'
              onClick={() => {
                if (data?.data) {
                  athleteTracking.trackInvoicePay(
                    data.data.invoiceId,
                    data.data.total / 100, // Convert from cents to dollars
                    data.data.dueDate,
                    data.data.autoPay,
                  );
                }
                // Set URL params for collect modal
                const params = new URLSearchParams(searchParams);
                params.set('collectModal', data.data.invoiceId);
                params.set('verb', 'pay');
                setSearchParams(params);
                openModal();
              }}
            >
              Pay Now
            </Button>
          )}
          {data?.data && ['PAID', 'REFUNDED'].includes(data.data.status) && (
            <Button color='kiloBlue' fullWidth size='md' radius='xl' onClick={handleSendInvoice}>
              Email Invoice
            </Button>
          )}
        </Stack>
        {invoiceId && (
          <InvoiceCollectModal
            invoiceId={invoiceId}
            opened={isModalOpened}
            close={handleCloseModal}
            verb='pay'
          />
        )}
      </Stack>
    </>
  );
};

export default InvoiceDetail;
